import { Chip, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { makeStyles } from '@mui/styles';
import {
  useGetTenantQuery,
  useUpdateTenantGeneralInfoMutation,
} from 'api/tenant.api';
import { useAvailableLanguages } from 'hooks/useAvailableLanguages';
import styles from 'assets/jss/material-dashboard-pro-react/views/tenantStyle.js';
import Button from 'components/CustomButtons/Button.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PromptAlert } from '../../features/ux/RouterPrompt';
import SupportedLanguagesModal from './SupportedLanguagesModal';

const useStyles = makeStyles(styles);

const currencyOptions = [
  'USD',
  'EUR',
  'GBP',
  'CAD',
  'MXN',
  'AUD',
  'NZD',
  'NOK',
  'SEK',
  'DKK',
];

export default function LocalizationTab({
  onSuccessTab,
  tab,
  setDirtyTabStatus,
  tenantStatus,
}) {
  const classes = useStyles();
  const { data: tenant } = useGetTenantQuery();
  const [updateGeneralInfo] = useUpdateTenantGeneralInfoMutation();
  const baseLanguages = useAvailableLanguages();

  const [viewIsDirty, setViewIsDirty] = useState(false);
  const [showWarningAlert, setShowWarningAlert] = useState(false);
  const [showLanguagesModal, setShowLanguagesModal] = useState(false);

  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [baseLocale, setBaseLocale] = useState('');
  const [baseLocaleState, setBaseLocaleState] = useState('');
  const [baseLocaleError, setBaseLocaleError] = useState('');

  const [baseCurrency, setBaseCurrency] = useState('');
  const [baseCurrencyState, setBaseCurrencyState] = useState('');
  const [baseCurrencyError, setBaseCurrencyError] = useState('');

  useEffect(() => {
    if (setDirtyTabStatus) {
      setDirtyTabStatus(viewIsDirty);
    }
  }, [viewIsDirty]);

  useEffect(() => {
    if (tenant) {
      // Initialize supported languages
      if (Array.isArray(tenant.supported_languages)) {
        setSupportedLanguages(tenant.supported_languages);
      } else {
        setSupportedLanguages([]);
      }

      // Initialize base locale
      if (tenant.base_locale) {
        setBaseLocale(tenant.base_locale);
      }

      // Initialize base currency
      if (tenant.base_currency) {
        setBaseCurrency(tenant.base_currency);
      }
    }
  }, [tenant]);

  const localeChangeHandler = (event) => {
    const newValue = event.target.value;
    if (newValue !== baseLocale) {
      setBaseLocale(newValue);
      setBaseLocaleState('success');
      setBaseLocaleError('');
      setViewIsDirty(true);
    }
  };

  const currencyChangeHandler = (event) => {
    const newValue = event.target.value;
    if (newValue !== baseCurrency) {
      setBaseCurrency(newValue);
      setBaseCurrencyState('success');
      setBaseCurrencyError('');
      setViewIsDirty(true);
    }
  };

  const handleOpenLanguagesModal = () => {
    setShowLanguagesModal(true);
  };

  const handleCloseLanguagesModal = () => {
    setShowLanguagesModal(false);
  };

  const handleSaveLanguages = async (languages) => {
    if (!tenant) {
      return;
    }

    try {
      // Update tenant with new supported languages
      const updatedTenant = cloneDeep(tenant);
      updatedTenant.supported_languages = languages;

      await updateGeneralInfo({ tenantGeneralInfo: updatedTenant }).unwrap();
      setSupportedLanguages(languages);

      // If base_locale is not in the new supported languages, clear it
      if (baseLocale && !languages.includes(baseLocale)) {
        setBaseLocale('');
        setBaseLocaleState('');
        setBaseLocaleError('');
      }

      toast.success('Supported languages updated successfully', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 2000,
        hideProgressBar: true,
      });
    } catch (err) {
      toast.error('An error occurred while updating supported languages', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 2000,
        hideProgressBar: true,
      });
      console.error('Error updating supported languages:', err);
    }
  };

  const revertForm = () => {
    if (!tenant) {
      return;
    }
    setBaseLocale(tenant.base_locale ?? '');
    setBaseLocaleState('');
    setBaseLocaleError('');
    setBaseCurrency(tenant.base_currency ?? '');
    setBaseCurrencyState('');
    setBaseCurrencyError('');
    setViewIsDirty(false);
  };

  const hasErrors = useCallback(() => {
    return [baseLocaleState, baseCurrencyState].some(
      (state) => state === 'error'
    );
  }, [baseCurrencyState, baseLocaleState]);

  const validateFields = () => {
    let error = false;
    if (!baseLocale || baseLocale === 'none') {
      setBaseLocaleState('error');
      setBaseLocaleError('Locale is required.');
      error = true;
    }

    if (!baseCurrency || baseCurrency === 'none') {
      setBaseCurrencyState('error');
      setBaseCurrencyError('Currency is required.');
      error = true;
    }

    return !error;
  };

  const saveChanges = async () => {
    if (!tenant) {
      return;
    }

    if (!validateFields()) {
      return;
    }

    try {
      const updatedTenant = cloneDeep(tenant);
      updatedTenant.base_locale = baseLocale;
      updatedTenant.base_currency = baseCurrency;

      await updateGeneralInfo({ tenantGeneralInfo: updatedTenant }).unwrap();
      setViewIsDirty(false);

      toast.success('Changes saved successfully', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 2000,
        hideProgressBar: true,
      });

      if (onSuccessTab) {
        onSuccessTab(tab.name);
      }
    } catch (err) {
      toast.error('An error occurred while trying to save the changes', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 2000,
        hideProgressBar: true,
      });
      console.error('Error saving localization data:', err);
    }
  };

  const renderLocaleSelect = () => {
    // If no supported languages configured, show all available languages as fallback
    const languagesToShow = supportedLanguages.length > 0 ? supportedLanguages : baseLanguages;
    
    const languageOptions = languagesToShow.map((language) => (
      <MenuItem key={language} value={language}>
        {language}
      </MenuItem>
    ));

    return (
      <Select
        variant='standard'
        className={classes.select}
        labelId='locale-select-label'
        id='locale-select'
        value={baseLocale}
        onChange={localeChangeHandler}
      >
        {languageOptions}
      </Select>
    );
  };

  const renderCurrencySelect = () => {
    return (
      <Select
        variant='standard'
        className={classes.select}
        labelId='currency-select-label'
        id='currency-select'
        value={baseCurrency}
        onChange={currencyChangeHandler}
        disabled={tenantStatus !== 'new'}
      >
        {currencyOptions.map((currency) => (
          <MenuItem key={currency} value={currency}>
            {currency}
          </MenuItem>
        ))}
      </Select>
    );
  };

  return (
    <GridContainer>
      {showWarningAlert && (
        <PromptAlert
          message='You have unsaved changes. Are you sure you want to discard them?'
          title='Discard Changes'
          onConfirm={() => {
            revertForm();
            setShowWarningAlert(false);
          }}
          onCancel={() => setShowWarningAlert(false)}
        />
      )}
      <SupportedLanguagesModal
        open={showLanguagesModal}
        onClose={handleCloseLanguagesModal}
        onSave={handleSaveLanguages}
        initialLanguages={supportedLanguages}
        availableLanguages={baseLanguages}
      />
      <GridItem xs={12}>
        <div style={{ paddingLeft: 11 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <span style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.75rem' }}>
              Supported Languages
            </span>
            <Button
              color='primary'
              onClick={handleOpenLanguagesModal}
              style={{ minWidth: 'auto', padding: '4px 12px', flexShrink: 0 }}
              data-testid="edit-languages-button"
            >
              <EditIcon style={{ marginRight: 4, fontSize: 16 }} />
              Edit
            </Button>
          </div>
          <div>
            {supportedLanguages && supportedLanguages.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {supportedLanguages.map((lang) => (
                  <Chip key={lang} label={lang} size='small' />
                ))}
              </div>
            ) : (
              <span style={{ color: '#999' }}>No languages selected</span>
            )}
          </div>
        </div>
      </GridItem>
      <GridItem xs={12}>
        <hr style={{ border: 'none', borderTop: '1px solid #e0e0e0', margin: '20px 0' }} />
      </GridItem>
      <GridItem xs={12} md={6}>
        <FormControl variant='standard' className={classes.formControl}>
          <InputLabel id='locale-select-label' className={classes.inputLabel}>
            Locale
          </InputLabel>
          {renderLocaleSelect()}
          {baseLocaleState === 'error' && (
            <p className={classes.errorText}>{baseLocaleError}</p>
          )}
        </FormControl>
      </GridItem>
      <GridItem xs={12} md={6} />
      <GridItem xs={12} md={6}>
        <FormControl variant='standard' className={classes.formControl}>
          <InputLabel id='currency-select-label' className={classes.inputLabel}>
            Currency
          </InputLabel>
          {renderCurrencySelect()}
          {baseCurrencyState === 'error' && (
            <p className={classes.errorText}>{baseCurrencyError}</p>
          )}
        </FormControl>
      </GridItem>
      <GridItem xs={12}>
        <Button
          color='rose'
          onClick={saveChanges}
          className={classes.registerButton}
          disabled={!viewIsDirty || hasErrors()}
        >
          Save Changes
        </Button>
        <Button
          color='secondary'
          onClick={() => setShowWarningAlert(true)}
          className={classes.registerButton}
          disabled={!viewIsDirty}
        >
          Cancel
        </Button>
      </GridItem>
    </GridContainer>
  );
}
